import { Suspense, useMemo } from 'react'
import type { ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Box, CircularProgress } from '@mui/material'
import { createBeoflowTheme } from '@/theme/muiTheme'
import { BeoflowThemeProvider } from '@/contexts/ThemeContext'
import { AuthProvider, isMobileWebViewAuthSession, useAuth } from '@/contexts/AuthContext'
import { AthleteProvider } from '@/contexts/AthleteContext'
import { ClipboardProvider } from '@/contexts/ClipboardContext'
import { LiveUpdatesProvider } from '@/contexts/LiveUpdatesContext'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import Layout from '@/components/layout/Layout'
import ImpersonationBanner from '@/components/ImpersonationBanner'
import { AuthenticatedAccessGate } from '@/components/subscription/AuthenticatedAccessGate'
import Navbar from '@/components/layout/Navbar'
import { NavbarProvider } from '@/contexts/NavbarProvider'
import { useNavbar } from '@/contexts/NavbarContext'
import { layout } from '@/theme/muiTheme'
import LensWebViewHeader from '@/components/lens/LensWebViewHeader'
import { lazyWithReload as lazy } from '@/lib/lazyWithReload'
import { captureAdAttribution } from '@/lib/attribution'
import { useUserProfile } from '@/services/api/settings'

// Capture ad-click attribution (fbclid/utm_*) from the landing URL once per
// page load, before any route renders (BEO-2929). Pure localStorage; no cookies.
captureAdAttribution()

// Lazy page imports — lazyWithReload recovers from stale chunks after a deploy
// (BEO-2381) by retrying the import once, then hard-reloading once per release.
const LoginPage = lazy(() => import('./pages/LoginPage'))
const LogoutPage = lazy(() => import('./pages/LogoutPage'))
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage'))
const SharedViewPage = lazy(() => import('./pages/SharedViewPage'))
const SharedFolderSharePage = lazy(() => import('./pages/SharedFolderSharePage'))
const SharedPlanSharePage = lazy(() => import('./pages/SharedPlanSharePage'))
const ChatPage = lazy(() => import('./pages/ChatPage'))
const ViewListPage = lazy(() => import('./pages/ViewListPage'))
const ViewDetailPage = lazy(() => import('./pages/ViewDetailPage'))
const LensViewEmbedPage = lazy(() => import('./pages/LensViewEmbedPage'))
const WorkoutTemplatesPage = lazy(() => import('./pages/WorkoutTemplatesPage'))
const ProgramsPage = lazy(() => import('./pages/ProgramsPage'))
const CoachPayoutsPage = lazy(() => import('./pages/CoachPayoutsPage'))
const CoachAlertsPage = lazy(() => import('./pages/CoachAlertsPage'))
const CoachTodayPage = lazy(() => import('./pages/CoachTodayPage'))
const SalesPage = lazy(() => import('./pages/SalesPage'))
const SuggestionsPage = lazy(() => import('./pages/SuggestionsPage'))
const DocPage = lazy(() => import('./pages/DocPage'))
const DocsListPage = lazy(() => import('./pages/DocsListPage'))
const ProgramSalePage = lazy(() => import('./pages/ProgramSalePage'))
const ProgramViewPage = lazy(() => import('./pages/ProgramViewPage'))
const ClaimProgramPage = lazy(() => import('./pages/ClaimProgramPage'))
const PurchaseCompletePage = lazy(() => import('./pages/PurchaseCompletePage'))
const RoutinesPage = lazy(() => import('./pages/RoutinesPage'))
const TeamOverviewPage = lazy(() => import('./pages/TeamOverviewPage'))
const InviteRedirectPage = lazy(() => import('./pages/InviteRedirectPage'))
const WorkoutBuilderPage = lazy(() => import('./pages/WorkoutBuilderPage'))
const ExerciseLibraryPage = lazy(() => import('./pages/ExerciseLibraryPage'))
const ExerciseDetailPage = lazy(() => import('./pages/ExerciseDetailPage'))
const CalendarPage = lazy(() => import('./pages/CalendarPage'))
const PeriodizationPage = lazy(() => import('./pages/PeriodizationPage'))
const DesignSystemPage = lazy(() => import('./design-system/DesignSystemPage'))
const PlanDetailPage = lazy(() => import('./pages/PlanDetailPage'))
const AccessPage = lazy(() => import('./pages/AccessPage'))
const InboxPage = lazy(() => import('./pages/InboxPage'))
const InboxProgramPage = lazy(() => import('./pages/InboxProgramPage'))
const SeatAccessFrozenPage = lazy(() => import('./pages/SeatAccessFrozenPage'))
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'))
const TrainingPeaksImportPage = lazy(() => import('./pages/TrainingPeaksImportPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const ExerciseZonesPage = lazy(() => import('./pages/ExerciseZonesPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'))
const AccountDeletePage = lazy(() => import('./pages/AccountDeletePage'))
const ActivityCommentPage = lazy(() => import('./pages/ActivityCommentPage'))
const CompleteProfilePage = lazy(() => import('./pages/CompleteProfilePage'))
const OnboardingLayout = lazy(() => import('./pages/OnboardingLayout'))
const MagicLoginPage = lazy(() => import('./pages/MagicLoginPage'))
const WizardPage = lazy(() => import('./pages/WizardPage'))
const McpInstallPage = lazy(() => import('./pages/McpInstallPage'))
const DesktopPage = lazy(() => import('./pages/DesktopPage'))
const DesktopSigninPage = lazy(() => import('./pages/DesktopSigninPage'))
const SecretDmaxCpCalculatorPage = lazy(() => import('./pages/SecretDmaxCpCalculatorPage'))
const RideBuilderPage = lazy(() => import('./pages/RideBuilderPage'))
const SecretPowerDurationPage = lazy(() => import('./pages/SecretPowerDurationPage'))
const ActivityDetailPage = lazy(() => import('./pages/ActivityDetailPage'))
const FieldTestModePage = lazy(() => import('./pages/FieldTestModePage'))
const ActivityReflectionPage = lazy(() => import('./pages/ActivityReflectionPage'))
const SleepReflectionPage = lazy(() => import('./pages/SleepReflectionPage'))
const SleepDashboardPage = lazy(() => import('./pages/SleepDashboardPage'))
const RecoveryDashboardPage = lazy(() => import('./pages/RecoveryDashboardPage'))
const ExerciseDashboardPage = lazy(() => import('./pages/ExerciseDashboardPage'))
const EmbeddedIntegrationsPage = lazy(() => import('./pages/EmbeddedIntegrationsPage'))
const EmbeddedWizardPage = lazy(() => import('./pages/EmbeddedWizardPage'))
const IntegrationOAuthReturnPage = lazy(() => import('./pages/IntegrationOAuthReturnPage'))
const KnowledgePage = lazy(() => import('./pages/KnowledgePage'))
const PaywallPage = lazy(() => import('./pages/PaywallPage'))
// const FitnessDashboardPage = lazy(() => import('./pages/FitnessDashboardPage'))

import { inviteUuidFromPath, rememberCoachInviteUuid } from '@/lib/pendingCoachInvite'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Compatibility export for the existing seat-freeze regression tests. The
// consolidated gate now applies plan entitlement before seat remediation.
export { AuthenticatedAccessGate as SeatGapGate } from '@/components/subscription/AuthenticatedAccessGate'

function RequireAuth({ children }: { children?: ReactNode }) {
  const { isAuthenticated, isImpExchangePending } = useAuth()
  // Impersonation handoff: hold rendering while the one-time code is being
  // exchanged on boot — otherwise the first render redirects to /login and the
  // user lands there instead of in the target's session.
  if (isImpExchangePending) {
    return <LoadingFallback />
  }
  if (!isAuthenticated) {
    const next = `${window.location.pathname.replace(import.meta.env.BASE_URL, '/')}${window.location.search}`.replace('//', '/')
    // Remember a coach invite before bouncing. `next` survives login but not
    // the login -> register -> verify-email -> onboarding detour a brand-new
    // athlete takes, and that athlete is exactly the one the invite is for.
    const inviteUuid = inviteUuidFromPath(next)
    if (inviteUuid) rememberCoachInviteUuid(inviteUuid)
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  }
  return <AuthenticatedAccessGate>{children}</AuthenticatedAccessGate>
}

/**
 * Coach-only routes. Both surfaces behind it read other people's health data,
 * so the entry point should not exist on an account that has no athletes.
 *
 * WAITS FOR THE PROFILE. `useUserProfile` starts undefined, and redirecting on
 * that first render would bounce every coach to the calendar before the answer
 * arrives. The gate is the account type, matching the Settings page and the
 * two sidebar entries that link here, so a visible link and a reachable route
 * cannot disagree. The BACKEND gate is consent, not account type
 * (apis/alerts/views.py, apis/program_suggestions/views.py): this is which
 * accounts get the screen, never what the screen is allowed to read.
 */
export function RequireCoachAccount() {
  const { data: profile, isPending } = useUserProfile()
  if (isPending) return <LoadingFallback />
  if (profile?.account_type !== 'professional-monitor') return <Navigate to="/calendar" replace />
  return <Outlet />
}

function LoadingFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'background.default' }}>
      <CircularProgress size={32} color="info" />
    </Box>
  )
}

function ProtectedLayout() {
  // iOS presents these pages inside its own full-screen cover with a native
  // header and back button, so the web sidebar and navbar would be a second,
  // conflicting chrome wrapped around the first. Same reasoning as ChatLayout
  // and LensWebViewLayout, which each opt out for the same reason.
  //
  // NavbarProvider is kept even when the chrome is not rendered: it is Layout
  // that supplies it, and anything calling useNavbar() below would throw
  // without it.
  const embedded = isMobileWebViewAuthSession()
  return (
    <RequireAuth>
      <AthleteProvider>
        {embedded ? (
          <NavbarProvider>
            <Outlet />
          </NavbarProvider>
        ) : (
          <Layout>
            <Outlet />
          </Layout>
        )}
      </AthleteProvider>
    </RequireAuth>
  )
}

/** Standalone full-bleed layout for the Flow chat page: auth + athlete context,
 *  but no app Sidebar / Navbar / FlowChatLauncher — the page owns its own chrome. */
function ChatLayout() {
  return (
    <RequireAuth>
      <AthleteProvider>
        <Box
          sx={{
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            bgcolor: 'background.default',
          }}
        >
          <Outlet />
        </Box>
      </AthleteProvider>
    </RequireAuth>
  )
}

/** Authenticated WebView surface with no app or onboarding chrome. */
function EmbeddedIntegrationsLayout() {
  return (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  )
}

function PublicWorkoutLayout() {
  const { heightOffset } = useNavbar()
  return (
    <AthleteProvider>
      <Navbar showBackButton={false} sidebarOffset="0px" />
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          pt: `${layout.navbarHeight + heightOffset}px`,
          transition: 'padding-top 0.2s ease',
        }}
      >
        <Outlet />
      </Box>
    </AthleteProvider>
  )
}

// Field Test Mode is a full-screen tool with its own custom topbar (not the
// shared Navbar/Layout chrome), so it gets its own layout route -- like Lens,
// but inheriting the ambient global theme (light-mode-first) rather than
// forcing dark; only its chart panel itself is always dark.
function FieldTestModeLayout() {
  return (
    <RequireAuth>
      {/* AthleteProvider so a coach viewing an athlete's activity can show whose
          data this is (the indicator chip in FieldTestTopBar). */}
      <AthleteProvider>
        <Outlet />
      </AthleteProvider>
    </RequireAuth>
  )
}

/** /labs/<rest>?<query> → /lens/<rest>?<query>, preserving the query string the
 *  iOS webview appends (athlete handover) and any hash. */
function LegacyLabsRedirect() {
  const location = useLocation()
  const target = location.pathname.replace(/^\/labs/, '/lens')
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />
}

/** /signup?email=..&next=/inbox/<uuid> → /register?...: the "create your free
 *  account with this address" email links the backend builds name the sign-up
 *  page /signup, which has always lived at /register. Preserve the address and
 *  the destination. */
function SignupRedirect() {
  const location = useLocation()
  return <Navigate to={`/register${location.search}${location.hash}`} replace />
}

function LensWebViewLayout() {
  // Lens is always dark (the iOS webview surface is black) regardless of the user's
  // global light/dark toggle.
  const lensDarkTheme = useMemo(() => createBeoflowTheme('dark'), [])
  return (
    <RequireAuth>
      <AthleteProvider>
        <NavbarProvider>
          <ThemeProvider theme={lensDarkTheme}>
            <Box
              sx={{
                minHeight: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                // Match the iOS wizard's pure-black surface (the Lens webview host).
                bgcolor: '#000000',
                color: 'text.primary',
              }}
            >
              <LensWebViewHeader />
              <Outlet />
            </Box>
          </ThemeProvider>
        </NavbarProvider>
      </AthleteProvider>
    </RequireAuth>
  )
}

// Like LensWebViewLayout but with NO web header at all: the iOS native top nav
// owns view switching and the date range on this surface, so any web chrome
// would double up with it.
function LensEmbedLayout() {
  const lensDarkTheme = useMemo(() => createBeoflowTheme('dark'), [])
  return (
    <RequireAuth>
      <AthleteProvider>
        <ThemeProvider theme={lensDarkTheme}>
          <Box
            sx={{
              minHeight: '100dvh',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#000000',
              color: 'text.primary',
            }}
          >
            <Outlet />
          </Box>
        </ThemeProvider>
      </AthleteProvider>
    </RequireAuth>
  )
}

function AppRoutes() {
  const location = useLocation()

  return (
    <ErrorBoundary>
      {/* Collapse onboarding sub-routes to one Suspense key so the onboarding
          layout (persistent lockup + stage progress) survives step navigation. */}
      <Suspense key={location.pathname.startsWith('/onboarding') ? '/onboarding' : location.pathname} fallback={<LoadingFallback />}>
        <Routes location={location}>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          {/* Beoflow Desktop intercepts this navigation; a browser landing here gets a pointer. */}
          <Route path="/desktop-signin" element={<DesktopSigninPage />} />
          {/* Public on purpose: hitting /logout while already signed out must
              redirect, not bounce through the auth guard. */}
          <Route path="/logout" element={<LogoutPage />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          {/* Shared views require sign-in: the viewer opens the template and
              fills it with THEIR OWN data (no athlete selection on this route). */}
          <Route path="/shared/:token" element={<RequireAuth><SharedViewPage /></RequireAuth>} />
          {/* BEO-3043 /r/ typed-path alias: /r/shared-view/<token> presents the
              same Labs shared-view page (kind shared-view, apis/widgets read). */}
          <Route path="/r/shared-view/:token" element={<RequireAuth><SharedViewPage /></RequireAuth>} />
          <Route element={<NavbarProvider><PublicWorkoutLayout /></NavbarProvider>}>
            <Route path="/workout/share/:publicWorkoutToken" element={<WorkoutBuilderPage />} />
            {/* BEO-3043 /r/ typed-path alias: the canonical destination URL the
                router/universal-link resolves to this SPA presentation page. */}
            <Route path="/r/shared-workout/:publicWorkoutToken" element={<WorkoutBuilderPage />} />
            {/* Shared folder share (kind shared-folder): public read, so it sits
                in the public layout like the workout share. The legacy
                /folder/share/<token> path aliases it so pre-router links don't
                404 (the backend's web_fallback_url points there). */}
            <Route path="/r/shared-folder/:token" element={<SharedFolderSharePage />} />
            <Route path="/folder/share/:token" element={<SharedFolderSharePage />} />
          </Route>
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/signup" element={<SignupRedirect />} />
          {/* Coach program sale (BEO-2980). Both routes are PUBLIC and stay
              outside RequireAuth: the sales page is the link a coach puts in
              their bio, and the claim page is where Stripe lands a buyer who
              does not have an account yet. destination/links.py emits
              /r/program-sale/<token> and falls back to /program/<token>;
              apis/plan_sales/checkout.py hardcodes the claim success URL. */}
          <Route path="/r/program-sale/:token" element={<ProgramSalePage />} />
          <Route path="/program/:token" element={<ProgramSalePage />} />
          {/* Read-only program share. PUBLIC and outside RequireAuth for the
              same reason as the sales page, and for one more: the whole point
              is that a physio or a parent with no Beoflow account can read it,
              and that having an account changes nothing about what they see.
              Putting either route behind RequireAuth would defeat the feature.
              destination/links.py emits /r/program-view/<token> and falls back
              to /program-view/<token>. */}
          <Route path="/r/program-view/:token" element={<ProgramViewPage />} />
          <Route path="/program-view/:token" element={<ProgramViewPage />} />
          <Route path="/r/claim/:claimToken" element={<ClaimProgramPage />} />
          {/* Stripe's success_url. PUBLIC and unparameterised: the buyer has
              just paid and usually has no account, and the claim token that
              opens the program is deliberately not in this URL. Without this
              route the catch-all sends a paying buyer to /calendar, which is
              behind RequireAuth, so every checkout ended at a login form. */}
          <Route path="/r/purchase-complete" element={<PurchaseCompletePage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* BEO-3043 Phase 7.2 auth kinds: the router's web_fallback_url for
              each auth kind carries the one-time key as a path segment; the
              /r/<kind>/<key> aliases keep the canonical destination URL
              presentable on the webapp host. Auth kinds are never
              destination-sheet targets: these SPA pages perform the auth
              action in the browser and only hand a typed post-auth
              destination back (MagicLoginPage emits the carried dest). */}
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/r/email-verify/:token" element={<VerifyEmailPage />} />
          <Route path="/reset-password/:token" element={<PasswordResetPage />} />
          <Route path="/r/password-reset/:token" element={<PasswordResetPage />} />
          <Route path="/magic-login/:magicToken" element={<MagicLoginPage />} />
          <Route path="/r/magic-login/:magicToken" element={<MagicLoginPage />} />
          <Route path="/account-delete/:token" element={<AccountDeletePage />} />
          <Route path="/r/account-delete/:token" element={<AccountDeletePage />} />
          <Route path="/complete-profile" element={<CompleteProfilePage />} />
          <Route path="/magic-login" element={<MagicLoginPage />} />
          <Route path="/integrations/oauth-return" element={<IntegrationOAuthReturnPage />} />
          <Route path="/design-system" element={<DesignSystemPage />} />
          {/* Legacy style-guide paths → unified design system */}
          <Route path="/style-guide" element={<Navigate to="/design-system" replace />} />
          <Route path="/style-guide-dark" element={<Navigate to="/design-system" replace />} />
          <Route element={<LensWebViewLayout />}>
            <Route path="/lens" element={<Navigate to="/lens/sleep" replace />} />
            <Route path="/lens/sleep" element={<SleepDashboardPage />} />
            <Route path="/lens/recovery" element={<RecoveryDashboardPage />} />
            <Route path="/lens/exercise" element={<ExerciseDashboardPage />} />
          </Route>
          <Route element={<LensEmbedLayout />}>
            <Route path="/lens/views" element={<LensViewEmbedPage />} />
            <Route path="/lens/views/:viewId" element={<LensViewEmbedPage />} />
          </Route>
          {/* Legacy /labs/* paths → /lens/*. Shipped iOS builds hardcode
              /labs/views AND append the athlete-handover query string, so this
              redirect is load-bearing until the Lens build is the floor — do
              not drop it on a rename cleanup. */}
          <Route path="/labs/*" element={<LegacyLabsRedirect />} />
          <Route element={<FieldTestModeLayout />}>
            <Route path="/activity/:activityId/field-test" element={<FieldTestModePage />} />
          </Route>

          {/* Public MCP install page — open to all users (staff gate removed). */}
          <Route path="/mcp-install" element={<McpInstallPage />} />
          {/* Public bike-session builder: the shipped notation editor, grid and
              shape chart with the sport locked to BIKING, exporting a ride link.
              Reads no athlete data and writes nothing, so it needs no session. */}
          <Route path="/ride/build" element={<RideBuilderPage />} />
          <Route path="/secret/dmax-cp" element={<SecretDmaxCpCalculatorPage />} />
          <Route path="/secret/power-duration" element={<SecretPowerDurationPage />} />

          {/* Athlete-track getting-started journey: persistent chrome (lockup +
              stage progress) wrapping the standalone steps. OnboardingLayout
              gates auth/account itself. */}
          <Route path="/onboarding/*" element={<OnboardingLayout />} />
          <Route element={<EmbeddedIntegrationsLayout />}>
            <Route path="/embed/integrations/v1" element={<EmbeddedIntegrationsPage />} />
            {/* The onboarding wizard for the native app. Same component the web
                onboarding runs, so the two cannot drift again. */}
            <Route path="/embed/wizard/v1" element={<EmbeddedWizardPage />} />
          </Route>
          {/* Standalone authed routes (own their full-screen chrome). */}
          <Route element={<RequireAuth />}>
            <Route path="/paywall" element={<PaywallPage />} />
            <Route path="/wizard" element={<WizardPage />} />
            {/* Opened in its own tab from the sidebar, so no app shell. */}
            <Route path="/integrations" element={<IntegrationsPage />} />
            {/* Knowledge base reader: login-gated, URL-only for now (no nav entry). */}
            <Route path="/knowledge/*" element={<KnowledgePage />} />
          </Route>

          {/* Invites are answered in Access now, never from the email. The old
              accept pages are redirects: the route STAYS (emails already sent
              and installed iOS builds open it), the element hands the destination
              kind and uuid to /access and leaves the answering to its cards.
              Signed out, RequireAuth sees the original /invite/ path first, so
              `next` stays the typed link and the exchange can still return its
              typed destination. */}
          <Route element={<RequireAuth />}>
            <Route path="/invite/:inviteUuid" element={<InviteRedirectPage kind="consent-invite" />} />
            <Route path="/team-invite/:inviteUuid" element={<InviteRedirectPage kind="team-invite" />} />
            <Route path="/plan-invite/:inviteUuid" element={<InviteRedirectPage kind="plan-editor-invite" />} />
            {/* BEO-3043 /r/ typed-path aliases: the router resolves these kinds
                to the same redirects as the legacy invite routes. */}
            <Route path="/r/consent-invite/:inviteUuid" element={<InviteRedirectPage kind="consent-invite" />} />
            <Route path="/r/team-invite/:inviteUuid" element={<InviteRedirectPage kind="team-invite" />} />
            <Route path="/r/plan-editor-invite/:inviteUuid" element={<InviteRedirectPage kind="plan-editor-invite" />} />
            {/* Shared plan share (kind shared-plan): the plan preview read is
                authenticated, so it sits with the authed invite pages. Legacy
                /plans/share/<token> aliases it (backend web_fallback_url). */}
            <Route path="/r/shared-plan/:token" element={<SharedPlanSharePage />} />
            <Route path="/plans/share/:token" element={<SharedPlanSharePage />} />
            {/* BEO-3043 §7.1 activity-comment deep link (desired row 9):
                the backend `activity-comment` kind resolves here. The page
                resolves the opaque key via the destination resolve API (403
                wrong_account / 404 not_found) BEFORE any render read, and
                emits the typed outcome from the data-success branch only.
                /activity-comment/<key> is the webapp fallback route, matching
                the backend's web_fallback_url (BEO-3043 §7.1). */}
            <Route path="/activity-comment/:key" element={<ActivityCommentPage />} />
            <Route path="/r/activity-comment/:key" element={<ActivityCommentPage />} />
          </Route>

          {/* Flow chat: standalone full-bleed surface (owns its chrome), not the app shell */}
          <Route element={<ChatLayout />}>
            <Route path="/chat" element={<ChatPage />} />
          </Route>

          {/* Protected routes with shared layout */}
          <Route element={<ProtectedLayout />}>
            <Route path="/" element={<Navigate to="/calendar" replace />} />
            <Route path="/views" element={<ViewListPage />} />
            <Route path="/views/:id" element={<ViewDetailPage />} />
            <Route path="/workouts" element={<WorkoutTemplatesPage />} />
            <Route path="/programs" element={<ProgramsPage />} />
            {/* One curated training plan, whole. Deeper than /plans/share/:token,
                so the share route still wins its own path. */}
            <Route path="/plans/:planId" element={<PlanDetailPage />} />
            {/* Both paths are the Stripe Account Link refresh_url / return_url
                in subscriptions/stripe/connect.py. Renaming either one here
                strands a coach mid-onboarding. */}
            <Route path="/coach/payouts/connect" element={<CoachPayoutsPage />} />
            <Route path="/coach/payouts/connected" element={<CoachPayoutsPage returned />} />
            {/* Pillars 2 and 3's delivery surfaces. The onboarding stages
                promise both of these by name ("Told when an athlete needs
                you", "each suggestion has an Apply button"), so they are the
                screens those sentences point at. */}
            <Route element={<RequireCoachAccount />}>
              {/* Today is the first Coaching-tools entry because it is the
                  question a coach opens the app with. It reads other people's
                  health data, so it sits behind the same account gate as the
                  two surfaces it summarises. */}
              <Route path="/coach/today" element={<CoachTodayPage />} />
              <Route path="/coach/alerts" element={<CoachAlertsPage />} />
              {/* A Settings sub-page: the coach's sales, where the earnings
                  panel lives now that it is no longer above the Access roster. */}
              <Route path="/settings/sales" element={<SalesPage />} />
              <Route path="/coach/suggestions" element={<SuggestionsPage />} />
            </Route>
            <Route path="/team" element={<TeamOverviewPage />} />
            <Route path="/workouts/builder" element={<WorkoutBuilderPage />} />
            <Route path="/workouts/builder/:templateId" element={<WorkoutBuilderPage />} />
            {/* Editing a scheduled session. Ranked above /builder/:templateId by
                its static "event" segment, so a numeric templateId still wins its
                own route. */}
            {/* `event/new` is a session that does not exist yet: the calendar
                passes date/sport/plan and the builder's first save creates it.
                Matches the same :eventId route -- the builder tells them apart. */}
            <Route path="/workouts/builder/event/:eventId" element={<WorkoutBuilderPage />} />
            <Route path="/workouts/execute/:templateId" element={<WorkoutBuilderPage />} />
            <Route path="/workouts/execute/event/:eventId" element={<WorkoutBuilderPage />} />
            <Route path="/calendar" element={<CalendarPage embedded={isMobileWebViewAuthSession()} />} />
            <Route path="/routines" element={<RoutinesPage />} />
            <Route path="/periodization" element={<PeriodizationPage />} />
            <Route path="/exercises" element={<ExerciseLibraryPage />} />
            <Route path="/exercises/:exerciseId" element={<ExerciseDetailPage />} />
            <Route path="/docs" element={<DocsListPage />} />
            <Route path="/docs/:id" element={<DocPage />} />
            <Route path="/access" element={<AccessPage />} />
            {/* One fixed page for everything addressed to you. Focused items
                live under it (`/inbox/<uuid>`); the redirects and every
                notification link resolve here. */}
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/inbox/:uuid/program" element={<InboxProgramPage />} />
            <Route path="/inbox/:uuid" element={<InboxPage />} />
            {/* TrainingPeaks import receiver: the page the Beoflow importer
                extension talks to. Authenticated (RequireAuth sends a logged-out
                coach to login and back with `next`), coach-facing but NOT
                RequireCoachAccount: nothing else on it reads other accounts. */}
            <Route path="/import/trainingpeaks" element={<TrainingPeaksImportPage />} />
            <Route path="/exercise-zones" element={<ExerciseZonesPage />} />
            <Route path="/desktop" element={<DesktopPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/billing/access-frozen" element={<SeatAccessFrozenPage />} />
            <Route path="/activity/:activityId" element={<ActivityDetailPage />} />
            <Route path="/activity/:activityId/reflect" element={<ActivityReflectionPage />} />
            <Route path="/sleep/:sleepId/reflect" element={<SleepReflectionPage />} />
            <Route path="/dashboard/sleep" element={<SleepDashboardPage />} />
            <Route path="/dashboard/recovery" element={<RecoveryDashboardPage />} />
            <Route path="/dashboard/exercise" element={<ExerciseDashboardPage />} />
            {/* <Route path="/dashboard/fitness" element={<FitnessDashboardPage />} /> */}
            <Route path="*" element={<Navigate to="/calendar" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BeoflowThemeProvider>
        <CssBaseline />
        <BrowserRouter basename={import.meta.env.BASE_URL}>
          <AuthProvider>
            {/* Above the route-keyed <Suspense> so the calendar copy/paste
                clipboard survives navigation between Programs and Calendar. */}
            <ClipboardProvider>
              {/* Single app-wide live-updates socket: refreshes Programs /
                  Calendar / Views when an MCP agent writes, + presence banner. */}
              <LiveUpdatesProvider>
                {/* Fixed overlay: renders on EVERY authenticated surface (some
                    routes use ChatLayout/EmbeddedIntegrationsLayout without
                    <Layout>, which is where the banner used to live). No-op
                    when not impersonating. */}
                <ImpersonationBanner />
                <AppRoutes />
              </LiveUpdatesProvider>
            </ClipboardProvider>
          </AuthProvider>
        </BrowserRouter>
      </BeoflowThemeProvider>
    </QueryClientProvider>
  )
}

export default App
